import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { CardBox } from '@/components/ui/CardBox';
import { Icon } from '@/components/ui/Icon';
import { mdiAlertCircle, mdiImport } from '@mdi/js';
import * as React from 'react';
import { ImportOptions } from './components/ImportOptions';
import { JobHistory } from './components/JobHistory';
import { JobProgress } from './components/JobProgress';
import { UrlInput } from './components/UrlInput';
import {
  useImportJobMutations,
  useImportJobProgress,
  useImportJobs,
  useProviders,
} from './hooks/useImportJobs';

type ImportStep = 'input' | 'options' | 'progress';

export const ImportPage: React.FC = () => {
  const [currentStep, setCurrentStep] = React.useState<ImportStep>('input');
  const [selectedProvider, setSelectedProvider] =
    React.useState<string>('google_maps');
  const [urls, setUrls] = React.useState<string>('');
  const [selectedCategoryIds, setSelectedCategoryIds] = React.useState<
    number[]
  >([]);
  const [selectedFilterIds, setSelectedFilterIds] = React.useState<number[]>(
    [],
  );
  const [selectedStatus, setSelectedStatus] = React.useState<string>('Pending');
  const [activeJobId, setActiveJobId] = React.useState<number | null>(null);

  const { data: providers = [], isLoading: isLoadingProviders } =
    useProviders();
  const { data: jobs = [] } = useImportJobs();
  const { data: activeJob } = useImportJobProgress(
    activeJobId,
    currentStep === 'progress',
  );

  const { createJob, pauseJob, resumeJob, cancelJob, retryFailed } =
    useImportJobMutations();

  const parsedUrls = React.useMemo(() => {
    if (!urls.trim()) return [];
    return urls
      .split(/[\n]+/)
      .map(url => url.trim())
      .filter(url => url.length > 0 && url.startsWith('http'));
  }, [urls]);

  const handleContinueToOptions = () => {
    if (parsedUrls.length > 0) {
      setCurrentStep('options');
    }
  };

  const handleStartImport = async () => {
    if (parsedUrls.length === 0) return;

    const result = await createJob.mutateAsync({
      provider: selectedProvider,
      urls: parsedUrls,
      categoryIds: selectedCategoryIds,
      filterIds: selectedFilterIds,
      businessStatus: selectedStatus,
    });

    setActiveJobId(result.id);
    setCurrentStep('progress');
  };

  const handleSelectJob = (jobId: number) => {
    setActiveJobId(jobId);
    setCurrentStep('progress');
  };

  const handleNewImport = () => {
    setUrls('');
    setActiveJobId(null);
    setSelectedCategoryIds([]);
    setSelectedFilterIds([]);
    setCurrentStep('input');
  };

  const selectedProviderData = providers.find(
    p => p.identifier === selectedProvider,
  );
  const isConfigured = selectedProviderData?.configured ?? false;

  if (isLoadingProviders) {
    return (
      <DashboardLayout>
        <div className='flex items-center justify-center h-64'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600' />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <ProtectedRoute action='create' subject='Business'>
        <div className='max-w-4xl mx-auto'>
          <div className='mb-6'>
            <h1 className='text-2xl font-bold text-gray-900 flex items-center'>
              <Icon path={mdiImport} size={28} className='mr-2' />
              Import Businesses
            </h1>
            <p className='text-gray-500 mt-1'>
              Bulk import businesses from map providers
            </p>
          </div>

          {providers.length === 0 || !isConfigured ? (
            <CardBox>
              <div className='text-center py-8'>
                <Icon
                  path={mdiAlertCircle}
                  size={48}
                  className='text-yellow-500 mx-auto mb-4'
                />
                <h2 className='text-lg font-medium text-gray-900 mb-2'>
                  No Map Provider Configured
                </h2>
                <p className='text-gray-500 max-w-md mx-auto'>
                  Please configure a map provider API key (e.g.,
                  GOOGLE_MAPS_API_KEY) to enable business imports.
                </p>
              </div>
            </CardBox>
          ) : (
            <>
              {currentStep !== 'progress' && (
                <div className='mb-6'>
                  <div className='flex items-center justify-between'>
                    {[
                      { key: 'input', label: 'URLs' },
                      { key: 'options', label: 'Options' },
                      { key: 'progress', label: 'Import' },
                    ].map((step, index) => {
                      const steps: ImportStep[] = [
                        'input',
                        'options',
                        'progress',
                      ];
                      const currentIndex = steps.indexOf(currentStep);
                      const stepIndex = index;
                      const isActive = currentStep === step.key;
                      const isCompleted = stepIndex < currentIndex;

                      return (
                        <React.Fragment key={step.key}>
                          <div className='flex flex-col items-center'>
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                isActive
                                  ? 'bg-blue-600 text-white'
                                  : isCompleted
                                    ? 'bg-green-500 text-white'
                                    : 'bg-gray-200 text-gray-500'
                              }`}
                            >
                              {index + 1}
                            </div>
                            <span
                              className={`text-xs mt-1 ${
                                isActive
                                  ? 'text-blue-600 font-medium'
                                  : 'text-gray-500'
                              }`}
                            >
                              {step.label}
                            </span>
                          </div>
                          {index < 2 && (
                            <div
                              className={`flex-1 h-0.5 mx-2 ${
                                stepIndex < currentIndex
                                  ? 'bg-green-500'
                                  : 'bg-gray-200'
                              }`}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              )}

              <CardBox>
                {currentStep === 'input' && (
                  <UrlInput
                    providers={providers}
                    selectedProvider={selectedProvider}
                    onProviderChange={setSelectedProvider}
                    urls={urls}
                    onUrlsChange={setUrls}
                    onSubmit={handleContinueToOptions}
                    isLoading={false}
                  />
                )}

                {currentStep === 'options' && (
                  <div className='space-y-6'>
                    <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
                      <p className='text-sm text-blue-700'>
                        <span className='font-semibold'>
                          {parsedUrls.length.toLocaleString()}
                        </span>{' '}
                        URLs ready to import
                      </p>
                    </div>

                    <ImportOptions
                      selectedCategoryIds={selectedCategoryIds}
                      selectedFilterIds={selectedFilterIds}
                      selectedStatus={selectedStatus}
                      onCategoryChange={setSelectedCategoryIds}
                      onFilterChange={setSelectedFilterIds}
                      onStatusChange={setSelectedStatus}
                    />

                    <div className='flex justify-between pt-4 border-t'>
                      <button
                        onClick={() => setCurrentStep('input')}
                        className='px-4 py-2 text-gray-600 hover:text-gray-900'
                      >
                        Back
                      </button>
                      <button
                        onClick={handleStartImport}
                        disabled={createJob.isPending}
                        className='px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'
                      >
                        {createJob.isPending
                          ? 'Starting...'
                          : `Start Import (${parsedUrls.length.toLocaleString()} items)`}
                      </button>
                    </div>
                  </div>
                )}

                {currentStep === 'progress' && activeJob && (
                  <JobProgress
                    job={activeJob}
                    onPause={() => pauseJob.mutate(activeJob.id)}
                    onResume={() => resumeJob.mutate(activeJob.id)}
                    onCancel={() => cancelJob.mutate(activeJob.id)}
                    onRetry={() => retryFailed.mutate(activeJob.id)}
                    onNewImport={handleNewImport}
                    isPausing={pauseJob.isPending}
                    isResuming={resumeJob.isPending}
                    isCancelling={cancelJob.isPending}
                    isRetrying={retryFailed.isPending}
                  />
                )}
              </CardBox>

              {currentStep === 'input' && jobs.length > 0 && (
                <JobHistory
                  jobs={jobs}
                  onSelectJob={handleSelectJob}
                  selectedJobId={activeJobId}
                />
              )}
            </>
          )}
        </div>
      </ProtectedRoute>
    </DashboardLayout>
  );
};
